import { useRef, useEffect, useState } from "react"

const JobRequirementsEditor = ({ value, onChange, error, minHeight = 400 }) => {
  const editorRef = useRef(null)
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    unorderedList: false,
    orderedList: false,
  })

  const updateActiveFormats = () => {
    if (!editorRef.current) return
    const selection = window.getSelection()
    if (!selection.rangeCount) return
    const range = selection.getRangeAt(0)
    if (!editorRef.current.contains(range.commonAncestorContainer)) return
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      unorderedList: document.queryCommandState("insertUnorderedList"),
      orderedList: document.queryCommandState("insertOrderedList"),
    })
  }

  const formatText = (command) => {
    document.execCommand(command, false, null)
    updateContent()
    updateActiveFormats()
    editorRef.current?.focus()
  }

  const updateContent = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"))
    updateContent()
  }

  const handleInput = () => {
    updateContent()
    updateActiveFormats()
  }

  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === "b") { e.preventDefault(); formatText("bold") }
      if (e.key === "i") { e.preventDefault(); formatText("italic") }
    }
  }

  const handleSelectionChange = () => updateActiveFormats()

  const handleClear = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = ""
      onChange("")
      editorRef.current.focus()
      setActiveFormats({ bold: false, italic: false, unorderedList: false, orderedList: false })
    }
  }

  useEffect(() => {
    if (editorRef.current && value) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value
      }
    }
    document.addEventListener("selectionchange", handleSelectionChange)
    return () => document.removeEventListener("selectionchange", handleSelectionChange)
  }, [value])

  const defaultPlaceholder = `Company Description
We suggest you enter details here.

Role Description
This is a full-time on-site role for a Software Engineer Intern.

Qualifications
• Basic understanding of Computer Science principles
• Proficiency in Back-End Web Development and Software Development
• Programming skills in Java, Python, or similar`

  const charCount = value ? value.replace(/<[^>]*>/g, "").trim().length : 0

  const ToolbarBtn = ({ active, onClick, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 30,
        height: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        border: `1px solid ${active ? "#111827" : "#e5e7eb"}`,
        borderRadius: 6,
        background: active ? "#111827" : "#fff",
        color: active ? "#fff" : "#6b7280",
        cursor: "pointer",
        fontFamily: "'DM Sans', sans-serif",
        transition: "all 0.12s",
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = "#9ca3af"
          e.currentTarget.style.color = "#111827"
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = "#e5e7eb"
          e.currentTarget.style.color = "#6b7280"
        }
      }}
    >
      {children}
    </button>
  )

  return (
    <div
      style={{
        overflow: "hidden",
        background: "#fff",
        border: `1px solid ${error ? "#f87171" : "#e5e7eb"}`,
        borderRadius: 4,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

        .jre-editor:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          white-space: pre-line;
          font-style: normal;
          font-size: 14px;
          line-height: 1.7;
        }
        .jre-editor:focus { outline: none; }
        .jre-editor p  { margin: 0.5em 0; }
        .jre-editor ul { margin: 0.5em 0; padding-left: 1.5em; list-style-type: disc; }
        .jre-editor ol { margin: 0.5em 0; padding-left: 1.5em; list-style-type: decimal; }
        .jre-editor li { margin: 0.25em 0; display: list-item; }
        .jre-editor strong { font-weight: 700; }
        .jre-editor em { font-style: italic; }
      `}</style>

      {/* ── Header bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid #f3f4f6",
          background: "#fff",
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#374151",
            letterSpacing: "-0.01em",
          }}
        >
          Job Requirements
        </span>
      </div>

      {/* ── Toolbar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 14px",
          borderBottom: "1px solid #f3f4f6",
          background: "#fafafa",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <ToolbarBtn
            active={activeFormats.bold}
            onClick={() => formatText("bold")}
            title="Bold (Ctrl+B)"
          >
            B
          </ToolbarBtn>

          <ToolbarBtn
            active={activeFormats.italic}
            onClick={() => formatText("italic")}
            title="Italic (Ctrl+I)"
          >
            <span style={{ fontStyle: "italic" }}>I</span>
          </ToolbarBtn>

          {/* Divider */}
          <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />

          <ToolbarBtn
            active={activeFormats.unorderedList}
            onClick={() => formatText("insertUnorderedList")}
            title="Bullet List"
          >
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
              <circle cx="3" cy="5" r="1.5" />
              <rect x="6" y="4" width="11" height="2" rx="1" />
              <circle cx="3" cy="10" r="1.5" />
              <rect x="6" y="9" width="11" height="2" rx="1" />
              <circle cx="3" cy="15" r="1.5" />
              <rect x="6" y="14" width="11" height="2" rx="1" />
            </svg>
          </ToolbarBtn>

          <ToolbarBtn
            active={activeFormats.orderedList}
            onClick={() => formatText("insertOrderedList")}
            title="Numbered List"
          >
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
              <text x="1" y="7" fontSize="6" fontWeight="600" fontFamily="monospace">1.</text>
              <rect x="6" y="4" width="11" height="2" rx="1" />
              <text x="1" y="12" fontSize="6" fontWeight="600" fontFamily="monospace">2.</text>
              <rect x="6" y="9" width="11" height="2" rx="1" />
              <text x="1" y="17" fontSize="6" fontWeight="600" fontFamily="monospace">3.</text>
              <rect x="6" y="14" width="11" height="2" rx="1" />
            </svg>
          </ToolbarBtn>
        </div>

        <button
          type="button"
          onClick={handleClear}
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "#9ca3af",
            background: "transparent",
            border: "1px solid transparent",
            borderRadius: 6,
            padding: "4px 10px",
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
            transition: "all 0.12s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#374151"
            e.currentTarget.style.borderColor = "#e5e7eb"
            e.currentTarget.style.background = "#fff"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#9ca3af"
            e.currentTarget.style.borderColor = "transparent"
            e.currentTarget.style.background = "transparent"
          }}
        >
          Clear
        </button>
      </div>

      {/* ── Editor area ── */}
      <div
        ref={editorRef}
        contentEditable={true}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onMouseUp={updateActiveFormats}
        onKeyUp={updateActiveFormats}
        className="jre-editor"
        style={{
          width: "100%",
          minHeight: `${minHeight}px`,
          maxHeight: `${minHeight * 2}px`,
          padding: "16px 18px",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.75,
          color: "#111827",
          background: "#fff",
          overflowY: "auto",
          fontFamily: "'DM Sans', sans-serif",
        }}
        data-placeholder={!value ? defaultPlaceholder : ""}
        suppressContentEditableWarning={true}
      />

      {/* ── Status bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "9px 16px",
          borderTop: "1px solid #f3f4f6",
          background: "#fafafa",
        }}
      >
        <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 400 }}>
          {charCount > 0
            ? "Ctrl+B bold · Ctrl+I italic"
            : "Start typing your job requirements..."}
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: charCount > 9000 ? "#ef4444" : "#9ca3af",
          }}
        >
          {charCount.toLocaleString()} / 10,000
        </span>
      </div>

      {/* ── Error ── */}
      {error && (
        <div
          style={{
            padding: "10px 16px",
            background: "#fef2f2",
            borderTop: "1px solid #fecaca",
          }}
        >
          <p style={{ fontSize: 13, color: "#ef4444", fontWeight: 500 }}>{error}</p>
        </div>
      )}
    </div>
  )
}

export default JobRequirementsEditor